import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, StatusPill } from "@/components/Bits";
import { inputCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const MODES = ["cash", "upi", "card", "insurance", "bank_transfer", "online", "cheque", "other"];

export default function LabTransactions() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ from_date: "", to_date: "", mode: "", kind: "", q: "" });
  const [page, setPage] = useState(1);

  const load = async (p = 1) => {
    setData(null); setPage(p);
    try {
      const { data } = await api.get("/lab/transactions", { params: { page: p, limit: 25, from_date: f.from_date || undefined, to_date: f.to_date || undefined, mode: f.mode || undefined, kind: f.kind || undefined, q: f.q || undefined } });
      setData(data);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  if (!can("lab.orders.view")) return <NoAccess what="lab transactions" />;
  const pages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;

  return (
    <div data-testid="lab-transactions-page">
      <PageHeader overline="Cases" title="Transactions" subtitle="Every payment recorded across bills — the authoritative money ledger." />
      <form onSubmit={(e) => { e.preventDefault(); load(1); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        <input type="date" className={inputCls} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="txn-from" />
        <input type="date" className={inputCls} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="txn-to" />
        <select className={inputCls} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="txn-mode"><option value="">All modes</option>{MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select>
        <input className={inputCls} placeholder="Patient / reg no." value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="txn-q" />
        <div className="flex gap-2"><button className={ghostBtnCls} data-testid="txn-search">Search</button><button type="button" className={ghostBtnCls} onClick={() => { setF({ from_date: "", to_date: "", mode: "", kind: "", q: "" }); load(1); }}>Clear</button></div>
      </form>
      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <>
          <div className="flex flex-wrap gap-3 mb-4" data-testid="txn-summary">
            {Object.entries(data.summary).map(([m, v]) => <div key={m} className="panel px-4 py-2 text-sm"><span className="text-slate-500 capitalize">{m.replace("_", " ")}: </span><span className="font-semibold">{rup(v)}</span></div>)}
          </div>
          <div className="panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">{["Reg", "Patient", "Referred by", "Date", "Time", "Amount", "Mode", "Type", "Ref", "By"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {data.items.length === 0 ? <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No transactions.</td></tr> :
                  data.items.map((t) => (
                    <tr key={t.txn_id} className="border-b border-slate-50" data-testid={`txn-row-${t.txn_id}`}>
                      <td className="px-4 py-3 font-mono">{t.reg_no}</td><td className="px-4 py-3">{t.patient_name}</td>
                      <td className="px-4 py-3 text-slate-500">{t.referrer_name || "—"}</td><td className="px-4 py-3 text-slate-500">{t.date}</td>
                      <td className="px-4 py-3 text-slate-500">{t.time}</td>
                      <td className={`px-4 py-3 font-medium ${t.kind === "refund" ? "text-red-600" : "text-slate-900"}`}>{t.kind === "refund" ? "-" : ""}{rup(t.amount)}</td>
                      <td className="px-4 py-3 capitalize">{t.mode.replace("_", " ")}</td><td className="px-4 py-3 capitalize">{t.kind}</td>
                      <td className="px-4 py-3 text-slate-500">{t.reference || "—"}</td><td className="px-4 py-3 text-slate-500 text-xs">{t.received_by}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>{data.total} transactions</span>
            <div className="flex gap-2"><button disabled={page <= 1} onClick={() => load(page - 1)} className={ghostBtnCls}>Prev</button><span className="px-3 py-2">Page {page}/{pages}</span><button disabled={page >= pages} onClick={() => load(page + 1)} className={ghostBtnCls}>Next</button></div>
          </div>
        </>
      )}
    </div>
  );
}
